<template>
  <div class="app-html">
    <ClientOnly>
      <TheContent
        v-for="content of contentHtml"
        :key="content.id"
        :elem="content"
      />
    </ClientOnly>
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import cloneDeep from 'lodash-es/cloneDeep';
  import TheContent from './TheContent';
  import type { TContent } from '~/types/common';
  import { ScriptManager } from '@devhacker/shared/utils/script/ScriptManager';
  import { useUserStore } from '~/features/user/store';

  const isQuizOrPollIframe = (item: TContent) =>
    item.type === 'iframe' &&
    (item.attributes.id?.startsWith('poll_') || item.attributes.id?.startsWith('quiz_'));

  export default defineNuxtComponent({
    name: 'AppHtml',

    components: { TheContent },

    props: {
      elem: {
        type: Object as PropType<TContent>,
        required: true,
      },
    },

    data() {
      return {
        scriptManager: null as ScriptManager | null,
      };
    },

    computed: {
      user() {
        return useUserStore().userData;
      },

      quizOrPollId(): string | undefined {
        return this.contentHtml.find(isQuizOrPollIframe)?.attributes?.id;
      },

      children(): TContent[] {
        return cloneDeep(this.elem.children);
      },

      contentHtml(): TContent[] {
        const excludeContentType = [
          // '#text',
          'script',
          '#script-content',
          '#comment',
        ];

        const filterContent = (content: TContent) => {
          if (!excludeContentType.includes(content.type)) {
            if (content.children.length) {
              return (content.children = content.children.filter(filterContent));
            }

            return true;
          }
        };

        return this.children.filter(filterContent).map((item) => {
          if (isQuizOrPollIframe(item)) {
            item.attributes.src += `?url=${encodeURIComponent(useRoute().path)}`;
          }
          return item;
        });
      },

      contentInlineScripts(): TContent[] {
        const filterInlineScripts = (content: TContent) => {
          if (content.type === 'script' && !content.attributes?.src) {
            return content.children.filter(filterInlineScripts);
          }
          return content.type === '#script-content';
        };

        return this.children.filter(filterInlineScripts);
      },

      contentScripts(): TContent[] {
        const filterScripts = (content: TContent) => {
          return content.type === 'script' && content.attributes?.src;
        };
        return this.children.filter(filterScripts);
      },
    },

    watch: {
      contentScripts() {
        this.addSrcScripts();
      },
      contentInlineScripts() {
        this.runInlineScripts();
      },
      'user.integer_id'(userId) {
        if (!this.quizOrPollId) return;

        const iframe = document.getElementById(this.quizOrPollId) as HTMLIFrameElement | null;
        if (!iframe) return;

        iframe.addEventListener(
          'load',
          () => {
            iframe.contentWindow?.postMessage({ type: 'userId', userId }, '*');
          },
          { passive: true },
        );
      },
    },

    mounted() {
      const logger = this.$logger.withScope('🥟 app-html');
      this.scriptManager = new ScriptManager(logger, logger.error);

      this.addSrcScripts();
      this.runInlineScripts();
    },

    beforeUnmount() {
      this.scriptManager?.cleanup();
    },

    methods: {
      addSrcScripts() {
        this.contentScripts.forEach((script: TContent) => {
          const scriptSrc = script.attributes?.url ?? script.attributes?.src;
          if (scriptSrc) this.scriptManager?.addSrcScript(scriptSrc, this.elem.attributes?.title);
        });
      },

      runInlineScripts() {
        this.contentInlineScripts.forEach((contentScript: TContent) => {
          contentScript.children.forEach((script: TContent) => {
            if (script.text) {
              this.scriptManager?.addInlineScript(script.text, this.elem.attributes?.title);
            }
          });
        });
      },
    },
  });
</script>

<style lang="scss" scoped>
  .app-html {
    iframe {
      margin: 0 auto;
    }
  }
</style>
